/**
 * Projects Page
 * Central hub for saved documents with project-based navigation.
 * Supports viewing, searching, cloud sync, and managing documents.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, FolderOpen, FileText, Layers, Sparkles, Users } from 'lucide-react';
import { PageLayout } from '@/components';
import { UnauthenticatedPage, AppPageHeader } from '@/components/ui';
import { Chrome } from '@/components/layout';
import { SyncProgressModal } from '@/components/SyncProgressModal';
import { useDocumentStorage } from '@/hooks/useDocumentStorage';
import { useProjectConfig } from '@/hooks/useProjectConfig';
import { useAuthContext } from '@/contexts/AuthContext';
import { usePendingJobs } from '@/contexts/PendingJobsContext';
import { useSharing } from '@/hooks/useSharing';
import type { SavedDocument } from '@/lib/types';

// Local imports
import {
  DocumentCard,
  PendingJobCard,
  DocumentModal,
  MoveDocumentModal,
  SourcesModal,
  DocumentTypeTabs,
  SearchBar,
  HeroSection,
  EmptyState,
  PendingInvitationsBanner,
  ProjectSelector,
} from './components';
import { useDocumentFilters, useDocumentModal, useCloudSync } from './hooks';

const DocsLanding: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [documents, setDocuments] = useState<SavedDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sourcesDoc, setSourcesDoc] = useState<SavedDocument | null>(null);

  const { isAuthenticated, isLoading: authLoading, appMode } = useAuthContext();
  const { projects } = useProjectConfig();
  const { pendingJobs } = usePendingJobs();

  // Sharing: pending invitations and shared projects
  const {
    sharedWithMe,
    pendingInvitations,
    acceptInvitation,
    declineInvitation,
  } = useSharing();

  const {
    isReady,
    isCloudEnabled,
    getAllDocuments,
    getDocumentsByProject,
    updateDocument,
    deleteDocument,
    localDocumentCount,
    isSyncing,
    syncError,
    syncToCloud,
  } = useDocumentStorage();

  // Get highlighted doc ID from URL
  const highlightedDocId = searchParams.get('doc');

  // Document filters hook
  const {
    selectedProjectId,
    selectedProject,
    selectedDocType,
    searchQuery,
    documentTypes,
    documentTypeCounts,
    filteredDocuments,
    handleProjectSelect,
    handleDocTypeSelect,
    setSearchQuery,
  } = useDocumentFilters({ documents, projects, isCloudEnabled });

  // Load documents based on selected project
  const loadDocuments = useCallback(async () => {
    if (!isReady) return;
    setIsLoading(true);
    try {
      const docs = selectedProjectId
        ? await getDocumentsByProject(selectedProjectId)
        : await getAllDocuments();
      setDocuments(docs);
    } catch (err) {
      console.error('Failed to load documents:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isReady, selectedProjectId, getAllDocuments, getDocumentsByProject]);

  // Document modal hook
  const {
    selectedDoc,
    editedContent,
    reviewMode,
    hasUnsavedChanges,
    isSaving,
    isDeleting,
    docToMove,
    isMoving,
    transcriptionContent,
    transcriptionLoading,
    transcriptionError,
    openDocument,
    openDocumentInEditMode,
    closeDocument,
    setReviewMode,
    handleContentEdit,
    handleSaveDocument,
    handleDeleteDocument,
    handleCheckboxToggle,
    openMoveModal,
    closeMoveModal,
    handleMoveDocument,
  } = useDocumentModal({
    updateDocument,
    deleteDocument,
    onDocumentsChange: loadDocuments,
  });

  // Cloud sync hook
  const { showSyncModal, syncResult, handleSyncClick, closeSyncModal } = useCloudSync({
    syncToCloud,
    onSyncSuccess: loadDocuments,
  });

  // Load documents on mount and when project changes
  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  // Reload documents when a pending job completes (reactive approach)
  const prevPendingCountRef = useRef(pendingJobs.length);
  useEffect(() => {
    if (pendingJobs.length < prevPendingCountRef.current) {
      loadDocuments();
    }
    prevPendingCountRef.current = pendingJobs.length;
  }, [pendingJobs.length, loadDocuments]);

  // Filter pending jobs by selected project
  const filteredPendingJobs = useMemo(() => {
    if (!selectedProjectId) {
      return pendingJobs;
    }
    return pendingJobs.filter((job) => job.projectId === selectedProjectId);
  }, [pendingJobs, selectedProjectId]);

  // Auto-select highlighted document from URL
  useEffect(() => {
    if (highlightedDocId && documents.length > 0 && !isLoading) {
      const doc = documents.find((d) => d.id === highlightedDocId);
      if (doc) {
        openDocument(doc);
      }
    }
  }, [highlightedDocId, documents, isLoading, openDocument]);

  // Determine empty state icon based on active filters
  const EmptyIcon = selectedDocType ? FileText : selectedProject ? FolderOpen : Layers;

  // Check if we have any content to display
  const hasContent = filteredDocuments.length > 0 || filteredPendingJobs.length > 0;

  // Auth loading
  if (authLoading) {
    return (
      <PageLayout>
        <div className="max-w-5xl mx-auto px-6 py-12">
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
          </div>
        </div>
      </PageLayout>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <UnauthenticatedPage
        icon={<Layers className="w-8 h-8 text-emerald-400" />}
        title={t('projects.unauthTitle')}
        description={t('projects.unauthDescription')}
      />
    );
  }

  return (
    <PageLayout variant="plain">
      {/* Page Header */}
      <Chrome component="appPageHeader">
        <AppPageHeader
          icon={<Layers className="w-4 h-4" />}
          badge={t('projects.badge')}
          subtitle={projects.length === 0
            ? t('projects.subtitleEmpty')
            : t('projects.subtitleDefault')}
        >
          <HeroSection
            projects={projects}
            selectedProjectId={selectedProjectId}
            onProjectSelect={handleProjectSelect}
            isCloudEnabled={isCloudEnabled}
            isReady={isReady}
            isAuthenticated={isAuthenticated}
            isSyncing={isSyncing}
            localDocumentCount={localDocumentCount}
            onSyncClick={handleSyncClick}
          />
        </AppPageHeader>
      </Chrome>

      {/* Main Content */}
      <section className="py-6 sm:py-8 bg-gradient-to-b from-slate-50 via-white to-indigo-50/30 min-h-[50vh]">
        <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
          {/* Project Selector (App Mode only) */}
          {appMode && projects.length > 0 && (
            <ProjectSelector
              projects={projects}
              selectedProjectId={selectedProjectId}
              onProjectSelect={handleProjectSelect}
            />
          )}

          {/* Pending Invitations */}
          <PendingInvitationsBanner
            invitations={pendingInvitations}
            onAccept={acceptInvitation}
            onDecline={declineInvitation}
          />

          {/* Document Type Tabs */}
          <DocumentTypeTabs
            documentTypes={documentTypes}
            selectedDocType={selectedDocType}
            documentTypeCounts={documentTypeCounts}
            totalDocuments={documents.length}
            onDocTypeSelect={handleDocTypeSelect}
          />

          {/* Search */}
          <SearchBar value={searchQuery} onChange={setSearchQuery} />

          {/* Pending Jobs */}
          {filteredPendingJobs.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-amber-500" />
                <span className="text-sm font-medium text-slate-600">
                  {t('projects.processing')} ({filteredPendingJobs.length})
                </span>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                {filteredPendingJobs.map((job) => (
                  <PendingJobCard key={job.jobId} job={job} />
                ))}
              </div>
            </div>
          )}

          {/* Documents Grid / Loading / Empty */}
          {isLoading || !isReady ? (
            <div className="flex items-center justify-center py-12 sm:py-16">
              <div className="text-center">
                <Loader2 className="w-7 h-7 sm:w-8 sm:h-8 animate-spin text-emerald-600 mx-auto mb-3 sm:mb-4" />
                <p className="text-sm sm:text-base text-slate-600">{t('projects.loadingDocuments')}</p>
              </div>
            </div>
          ) : hasContent ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 pb-4">
              {filteredDocuments.map((doc) => (
                <DocumentCard
                  key={doc.id}
                  document={doc}
                  isSelected={selectedDoc?.id === doc.id}
                  isDeleting={isDeleting}
                  onView={openDocument}
                  onEdit={openDocumentInEditMode}
                  onMove={openMoveModal}
                  onSources={setSourcesDoc}
                  onDelete={handleDeleteDocument}
                />
              ))}
            </div>
          ) : (
            <EmptyState
              icon={EmptyIcon}
              searchQuery={searchQuery}
              selectedProject={selectedProject}
              selectedDocType={selectedDocType}
            />
          )}

          {/* Shared With Me */}
          {sharedWithMe.length > 0 && (
            <div className="mt-8 pt-6 border-t border-slate-200">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-5 h-5 text-indigo-600" />
                <h2 className="text-lg font-semibold text-slate-900">
                  {t('projects.sharedWithMe')}
                </h2>
                <span className="text-sm text-slate-500">({sharedWithMe.length})</span>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                {sharedWithMe.map((shared) => (
                  <div
                    key={shared.shareId}
                    className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => navigate(`/shared/${shared.projectId}`)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-indigo-50">
                        <FolderOpen className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-slate-900 truncate">
                          {shared.projectName}
                        </h3>
                        <p className="text-sm text-slate-500 mt-0.5">
                          {t('projects.sharedBy', { email: shared.ownerEmail })}
                        </p>
                        <span className="inline-block mt-1.5 px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-50 text-indigo-700">
                          {shared.role === 'editor' ? t('projects.roleEditor') : t('projects.roleViewer')}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Document Modal */}
      {selectedDoc && (
        <DocumentModal
          document={selectedDoc}
          editedContent={editedContent}
          reviewMode={reviewMode}
          hasUnsavedChanges={hasUnsavedChanges}
          isSaving={isSaving}
          isDeleting={isDeleting}
          transcriptionContent={transcriptionContent}
          transcriptionLoading={transcriptionLoading}
          transcriptionError={transcriptionError}
          onClose={closeDocument}
          onContentChange={handleContentEdit}
          onModeChange={setReviewMode}
          onSave={handleSaveDocument}
          onDelete={handleDeleteDocument}
          onCheckboxToggle={handleCheckboxToggle}
        />
      )}

      {/* Move Document Modal */}
      {docToMove && (
        <MoveDocumentModal
          document={docToMove}
          projects={projects}
          isMoving={isMoving}
          onMove={handleMoveDocument}
          onClose={closeMoveModal}
        />
      )}

      {/* Sources Modal */}
      {sourcesDoc && (
        <SourcesModal
          document={sourcesDoc}
          onClose={() => setSourcesDoc(null)}
        />
      )}

      {/* Sync Progress Modal */}
      <SyncProgressModal
        isOpen={showSyncModal}
        onClose={closeSyncModal}
        isSyncing={isSyncing}
        result={syncResult}
        error={syncError}
      />

    </PageLayout>
  );
};

export default DocsLanding;
